/**
 * Page-range logic shared by the v2 {@link RingilPaginator} and the compact v3 paginator. Plain `.ts`
 * so it can be unit-tested without React.
 */

export type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const range = (start: number, end: number): number[] => (start > end ? [] : Array.from({ length: end - start + 1 }, (_, i) => start + i));

/**
 * MUI-style pagination range: always show the first/last page and a window of `siblingCount` pages
 * around the current one, collapsing the gaps into ellipses. boundaryCount=1, siblingCount=2 matches
 * the Figma variants (e.g. 30 pages on page 1 → 1 2 3 4 5 6 7 … 30).
 */
export const buildPaginationItems = (current: number, totalPages: number): PaginationItem[] => {
  const boundaryCount = 1;
  const siblingCount = 2;

  if (totalPages <= 1) return [1];

  const startPages = range(1, Math.min(boundaryCount, totalPages));
  const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);

  const siblingsStart = Math.max(
    Math.min(current - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(current + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages.length > 0 ? endPages[0] - 2 : totalPages - 1,
  );

  return [
    ...startPages,
    ...(siblingsStart > boundaryCount + 2 ? ['start-ellipsis' as const] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < totalPages - boundaryCount - 1 ? ['end-ellipsis' as const] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
    ...endPages,
  ];
};
